// /login — email + password. POST → /v1/auth/login on services/auth.
// Server returns { step, flow_session }; we navigate to the matching subroute.
//
// This is the leaf "index" route under the /login layout (login.tsx).
// The layout exists so dot-notated children (login.password-change.tsx etc.)
// can render their own content via <Outlet/>.

import { ErrorBanner, FormField, LoginShell } from '@/components/LoginShell';
import { ApiError, authFetch } from '@/lib/api';
import { useAuth } from '@/state/auth';
import type { Me } from '@eidollon/contracts/auth';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

export const Route = createFileRoute('/login/')({
  component: LoginPage,
});

// /v1/auth/login returns one of:
//   - { step: "password_change_required" | "mfa_enroll_required" | "mfa_required",
//       flow_session: string }
//   - { step: "authenticated", access_token, expires_in, me }   ← dev MFA bypass only
type LoginResponse =
  | {
      step: 'password_change_required' | 'mfa_enroll_required' | 'mfa_required';
      flow_session: string;
    }
  | { step: 'authenticated'; access_token: string; expires_in: number; me: Me };

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const setFlow = useAuth((s) => s.setFlow);
  const setAccess = useAuth((s) => s.setAccess);
  const navigate = useNavigate();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const data = await authFetch<LoginResponse>('/v1/auth/login', {
        method: 'POST',
        body: { email, password },
      });
      switch (data.step) {
        case 'password_change_required':
          setFlow(data.flow_session, data.step);
          void navigate({ to: '/login/password-change' });
          break;
        case 'mfa_enroll_required':
          setFlow(data.flow_session, data.step);
          void navigate({ to: '/login/mfa-enroll' });
          break;
        case 'mfa_required':
          setFlow(data.flow_session, data.step);
          void navigate({ to: '/login/mfa-verify' });
          break;
        case 'authenticated':
          // Dev MFA bypass path — login already minted access + refresh.
          setAccess(data.access_token, data.me);
          void navigate({ to: '/intel' });
          break;
        default:
          setErr('internal_error');
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.code : 'internal_error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <LoginShell
      step="STEP 1 / 4 · IDENTITY"
      title="Sign in to Eidollon"
      sub="Enterprise terminal. No public signup. If you don't have an account, contact your tenant administrator."
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <ErrorBanner code={err} />
        <FormField label="EMAIL">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            style={{ flex: 1 }}
          />
        </FormField>
        <FormField label="PASSWORD" mono>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            style={{ flex: 1, fontFamily: 'var(--mono)' }}
          />
        </FormField>
        <button
          type="submit"
          className="btn pri"
          disabled={busy}
          style={{ height: 32, justifyContent: 'center', marginTop: 4 }}
        >
          {busy ? 'AUTHENTICATING…' : 'CONTINUE'}
        </button>
      </form>
    </LoginShell>
  );
}
